import React, { memo, useState, useCallback, useMemo, useEffect } from "react";
import { Paper, Typography, IconButton } from "@mhome/ui";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import StopIcon from "@mui/icons-material/Stop";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { MessageSquareReply } from "lucide-react";
import { processTags, ProcessedContent } from "./chat-util";
import {
  convertTextWithXmlTagsToHtml,
  MentionContentEditable,
} from "../common/mention-util";
import Markdown from "../common/markdown";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useCustomFormat } from "../views/after-init";
import { useEventEmitter } from "../ws/event-context";
import { useRecoilValue } from "recoil";
import { dialogsState } from "../global-state";
import dayjs from "dayjs";

// Operation constants
const OPERATIONS = {
  PUT: 1,
  DELETE: 2,
  FOLD: 4,
  UNFOLD: 8,
  HIGHLIGHT: 16,
  LOWLIGHT: 32,
};

// Message type constants
const MESSAGE_TYPES = {
  OP: "OP",
  REQ_STT: "REQ_STT",
  DEBUG: "DEBUG",
  TEXT: "TEXT",
  ERROR: "ERROR",
  WARN: "WARN",
  SUM: "SUM",
  LLM_REQ: "LLM_REQ",
  LLM_RESP: "LLM_RESP",
  EXPERT_START: "EXPERT_START",
  EXPERT_COMPLETE: "EXPERT_COMPLETE",
  EXPERT_ERROR: "EXPERT_ERROR",
  EXPERT_WARN: "EXPERT_WARN",
  EXPERT_STOPPED: "EXPERT_STOPPED",
  REQ: "REQ",
  REQ_ASSISTANT: "REQ_ASSISTANT",
};

// Non-foldable types
const NON_FOLDABLE_TYPES = new Set([
  MESSAGE_TYPES.DEBUG,
  MESSAGE_TYPES.TEXT,
  MESSAGE_TYPES.WARN,
  MESSAGE_TYPES.ERROR,
  MESSAGE_TYPES.SUM,
]);

const ChatBlock = memo(
  React.forwardRef(({ block, onAssistantInteraction, isStopped }, ref) => {
    const [expanded, setExpanded] = useState(true);
    const [stepExpansionOverrides, setStepExpansionOverrides] = useState({});
    const [isComplete, setIsComplete] = useState(false);
    const [isStoppedLocal, setIsStoppedLocal] = useState(false);
    const [currentTime, setCurrentTime] = useState(dayjs());
    const getComponentBgColor = useComponentBackgroundColor();
    const { emit } = useEventEmitter();
    const dialogs = useRecoilValue(dialogsState);
    const customFormat = useCustomFormat();

    // Check if a dialog message still exists in the global dialogs state
    const isDialogStillActive = useCallback(
      (message) => {
        if (!message || message.type !== "DIALOG" || !message.id) {
          return false;
        }
        return dialogs.some((dialog) => dialog.id === message.id);
      },
      [dialogs]
    );

    // Calculate remaining time for dialog countdown
    const calculateDialogRemainingTime = useCallback((createdAt, timeout) => {
      if (!createdAt || !timeout) return 0;
      const now = dayjs();
      const expiryTime = dayjs(createdAt * 1000).add(timeout, "second");
      return Math.max(0, expiryTime.diff(now, "second"));
    }, []);

    // Format countdown time display
    const formatCountdown = useCallback((remainingSeconds) => {
      if (remainingSeconds <= 0) return "Expired";

      const days = Math.floor(remainingSeconds / (24 * 60 * 60));
      const hours = Math.floor((remainingSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((remainingSeconds % (60 * 60)) / 60);
      const seconds = remainingSeconds % 60;

      if (days > 0) {
        return `${days}d`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else {
        return `${seconds}s`;
      }
    }, []);

    // Get dialog data from global state
    const getDialogData = useCallback(
      (message) => {
        if (!message || message.type !== "DIALOG" || !message.id) {
          return null;
        }
        return dialogs.find((dialog) => dialog.id === message.id);
      },
      [dialogs]
    );

    // Expose completion and stopped functions to parent
    React.useImperativeHandle(ref, () => ({
      markComplete: () => {
        setIsComplete(true);
      },
      markStopped: () => {
        setIsStoppedLocal(true);
      },
    }));

    // Update current time for dialog countdown
    useEffect(() => {
      const hasDialogsWithTimeout = block?.messages?.some(
        (message) => message.type === "DIALOG" && isDialogStillActive(message)
      );

      if (hasDialogsWithTimeout) {
        const interval = setInterval(() => {
          setCurrentTime(dayjs());
        }, 1000); // Update every second for active dialogs

        return () => clearInterval(interval);
      }
    }, [block?.messages, isDialogStillActive]);

    // Handle service message blocks differently
    if (block && block.from === "service") {
      const message = block.messages[0];

      return (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              overflow: "hidden",
              borderRadius: "16px",
              backgroundColor: getComponentBgColor("hsl(var(--muted))"),
            }}
          >
            <div
              style={{
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
            >
              {message.format === "PLAIN" && message.content && (
                <Typography
                  variant="body2"
                  style={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                  }}
                >
                  {message.content}
                </Typography>
              )}
              {/* TODO: user metter markdown later */}
              {message.format === "MARKDOWN" && message.content && (
                <div
                  style={
                    {
                      // Markdown styles will be handled by the Markdown component
                    }
                  }
                  className="markdown-container"
                >
                  <style>
                    {`
                      .markdown-container p {
                        margin: 0;
                        margin-bottom: 8px;
                        overflow-wrap: break-word;
                      }
                      .markdown-container pre {
                        white-space: pre-wrap;
                        overflow-wrap: break-word;
                      }
                      .markdown-container h1,
                      .markdown-container h2,
                      .markdown-container h3,
                      .markdown-container h4,
                      .markdown-container h5,
                      .markdown-container h6 {
                        margin-top: 8px;
                        margin-bottom: 8px;
                      }
                      .markdown-container ul,
                      .markdown-container ol {
                        margin-top: 4px;
                        margin-bottom: 4px;
                        padding-left: 16px;
                      }
                      .markdown-container blockquote {
                        border-left: 3px solid hsl(var(--border));
                        padding-left: 8px;
                        margin-left: 0;
                        margin-top: 4px;
                        margin-bottom: 4px;
                      }
                    `}
                  </style>
                  <Markdown>{message.content}</Markdown>
                </div>
              )}
              {/* TODO: user metter HTML later */}
              {message.format === "HTML" && message.content && (
                <>
                  <style>
                    {`
                      .html-content-wrapper p {
                        margin: 0;
                        margin-bottom: 8px;
                        overflow-wrap: break-word;
                      }
                      .html-content-wrapper pre {
                        white-space: pre-wrap;
                        overflow-wrap: break-word;
                      }
                      .html-content-wrapper h1,
                      .html-content-wrapper h2,
                      .html-content-wrapper h3,
                      .html-content-wrapper h4,
                      .html-content-wrapper h5,
                      .html-content-wrapper h6 {
                        margin-top: 8px;
                        margin-bottom: 8px;
                      }
                      .html-content-wrapper ul,
                      .html-content-wrapper ol {
                        margin-top: 4px;
                        margin-bottom: 4px;
                        padding-left: 16px;
                      }
                      .html-content-wrapper blockquote {
                        border-left: 3px solid hsl(var(--border));
                        padding-left: 8px;
                        margin-left: 0;
                        margin-top: 4px;
                        margin-bottom: 4px;
                      }
                    `}
                  </style>
                  <div
                    className="html-content-wrapper"
                    dangerouslySetInnerHTML={{ __html: message.content }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Handle notification message blocks
    if (block && block.from === "notification") {
      return (
        <div style={{ marginBottom: "16px" }}>
          {block.messages.map((message, index) => {
            const isDialog = message.type === "DIALOG";
            const isActive = isDialog ? isDialogStillActive(message) : false;
            const dialogData = isDialog ? getDialogData(message) : null;
            const remainingTime = dialogData
              ? calculateDialogRemainingTime(
                  dialogData.createdAt,
                  dialogData.timeout
                )
              : 0;

            return (
              <Paper
                key={`notification-${index}`}
                elevation={0}
                style={{
                  overflow: "hidden",
                  borderRadius: "16px",
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  marginBottom: index < block.messages.length - 1 ? "4px" : "0",
                  border: isDialog
                    ? isActive
                      ? "1px solid hsl(var(--yellow-500))"
                      : "none"
                    : "none",
                  boxShadow: "none",
                  position: "relative",
                  cursor: isDialog && isActive ? "pointer" : "default",
                  opacity: isDialog && !isActive ? 0.6 : 1,
                  paddingRight: isDialog ? "48px" : "16px",
                }}
                onClick={
                  isDialog && isActive
                    ? () => {
                        // Emit dialog click event to be handled by notification-dialog-system
                        console.log("dialog-click", message);
                        emit("dialog-click", { dialog: message });
                      }
                    : undefined
                }
                onMouseEnter={(e) => {
                  if (isDialog && isActive) {
                    e.currentTarget.style.backgroundColor = getComponentBgColor(
                      "hsl(var(--yellow-500) / 0.8)"
                    );
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDialog && isActive) {
                    e.currentTarget.style.backgroundColor =
                      getComponentBgColor("hsl(var(--card))");
                  }
                }}
              >
                {/* Countdown timer or expired icon for dialogs */}
                {isDialog && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "8px",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "40px",
                    }}
                  >
                    {isActive &&
                      // Active dialog - show countdown timer or open icon
                      remainingTime > 0 && (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-flex",
                          }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={
                              dialogData?.timeout
                                ? (remainingTime / dialogData.timeout) * 100
                                : 0
                            }
                            size={32}
                            thickness={4}
                            style={{
                              color:
                                remainingTime <= 60
                                  ? "hsl(var(--destructive))"
                                  : remainingTime <= 600
                                  ? "hsl(var(--yellow-500))"
                                  : "hsl(var(--primary))",
                            }}
                          />
                          <div
                            style={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: "absolute",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              component="div"
                              style={{
                                color:
                                  remainingTime <= 60
                                    ? "hsl(var(--destructive))"
                                    : remainingTime <= 600
                                    ? "hsl(var(--yellow-500))"
                                    : "hsl(var(--muted-foreground))",
                                fontWeight: remainingTime <= 60 ? 600 : 400,
                                fontSize: "0.6rem",
                              }}
                            >
                              {formatCountdown(remainingTime)}
                            </Typography>
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {/* Notification type indicator */}
                {!isDialog ? (
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "6px",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <NotificationsIcon style={{ fontSize: "1rem" }} />
                  </div>
                ) : (
                  !isActive && (
                    <div
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MessageSquareReply size={16} />
                    </div>
                  )
                )}

                <div
                  style={{
                    paddingLeft: "16px",
                    paddingRight: isDialog && isActive ? "56px" : "16px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  }}
                >
                  {/* Title for notifications and dialogs */}
                  {message.title && (
                    <Typography
                      variant={"subtitle2"}
                      style={{
                        fontWeight: 600,
                        marginBottom: message.content ? "4px" : "2px",
                        color: "hsl(var(--foreground))",
                        fontSize: "0.8rem",
                      }}
                    >
                      {message.title}
                    </Typography>
                  )}

                  {/* Content */}
                  {message.content && (
                    <Typography
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        color:
                          isDialog && !isActive
                            ? "hsl(var(--muted-foreground))"
                            : "hsl(var(--foreground))",
                        lineHeight: 1.4,
                        marginBottom: "4px",
                        fontSize: "0.8rem",
                        textDecoration:
                          isDialog && !isActive ? "line-through" : "none",
                      }}
                    >
                      {message.content}
                    </Typography>
                  )}

                  {/* Timestamp */}
                  <Typography
                    variant="caption"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                      fontSize: "0.65rem",
                      fontStyle: "italic",
                    }}
                  >
                    {customFormat(message.timestamp, dayjs())}
                  </Typography>
                </div>
              </Paper>
            );
          })}
        </div>
      );
    }

    // Process messages to group by step and apply operations
    const processedSteps = useMemo(() => {
      if (!block || !block.messages || block.messages.length === 0) {
        return {};
      }

      const messagesByStep = {};
      block.messages
        .filter(
          (message) =>
            message.type !== MESSAGE_TYPES.REQ &&
            message.type !== MESSAGE_TYPES.REQ_ASSISTANT &&
            message.type !== MESSAGE_TYPES.REQ_STT
        ) // Filter out REQ messages for separate processing
        .forEach((message) => {
          const stepId = message.step;
          if (!messagesByStep[stepId]) {
            messagesByStep[stepId] = [];
          }
          messagesByStep[stepId].push(message);
        });

      // Sort messages within each step by order
      Object.keys(messagesByStep).forEach((stepId) => {
        messagesByStep[stepId].sort((a, b) => (a.order || 0) - (b.order || 0));
      });

      // Apply operations in order to build final step state
      const finalSteps = {};

      Object.keys(messagesByStep).forEach((stepId) => {
        const messages = messagesByStep[stepId];
        let stepState = {
          step: parseInt(stepId),
          parentStep: -1,
          content: "",
          type: "TEXT",
          isHighlighted: false,
          isLowlighted: false,
          isFolded: false,
          exists: false,
          expertState: null, // Track expert state: 'start', 'complete', 'error'
          expertContent: "", // Store expert content separately
          elapsed: null, // Track elapsed time from the last PUT operation
        };

        // Apply operations in order
        messages.forEach((message) => {
          const operation = message.operation || 0;

          if (operation & OPERATIONS.PUT) {
            stepState.exists = true;
            if (message.content !== null && message.content !== undefined) {
              stepState.content = message.content;
            }
            if (message.type !== null && message.type !== undefined) {
              stepState.type = message.type;

              // Handle expert state tracking
              if (message.type === MESSAGE_TYPES.EXPERT_START) {
                stepState.expertState = "start";
                stepState.expertContent = message.content || "";
              } else if (message.type === MESSAGE_TYPES.EXPERT_COMPLETE) {
                stepState.expertState = "complete";
                if (message.content) stepState.expertContent = message.content;
                stepState.isFolded = true; // Auto-fold when expert completes
              } else if (message.type === MESSAGE_TYPES.EXPERT_ERROR) {
                stepState.expertState = "error";
                if (message.content) stepState.expertContent = message.content;
              } else if (message.type === MESSAGE_TYPES.EXPERT_WARN) {
                stepState.expertState = "warn";
                if (message.content) stepState.expertContent = message.content;
              } else if (message.type === MESSAGE_TYPES.EXPERT_STOPPED) {
                stepState.expertState = "stopped";
                if (message.content) stepState.expertContent = message.content;
              }
            }
            if (
              message.parentStep !== null &&
              message.parentStep !== undefined
            ) {
              stepState.parentStep = message.parentStep;
            }
            // Track elapsed time from PUT operations
            if (message.elapsed !== null && message.elapsed !== undefined) {
              if (message.type === MESSAGE_TYPES.EXPERT_START) {
                // Always set elapsed time for EXPERT_START
                stepState.elapsed = message.elapsed;
              } else if (
                message.type === MESSAGE_TYPES.EXPERT_COMPLETE ||
                message.type === MESSAGE_TYPES.EXPERT_ERROR ||
                message.type === MESSAGE_TYPES.EXPERT_WARN
              ) {
                // For EXPERT_COMPLETE, EXPERT_ERROR, and EXPERT_WARN, only set elapsed time if there isn't one already
                if (stepState.elapsed === null) {
                  stepState.elapsed = message.elapsed;
                }
              } else {
                // For non-expert states, always update elapsed time
                stepState.elapsed = message.elapsed;
              }
            }
          }

          if (operation & OPERATIONS.DELETE) {
            stepState.exists = false;
          }

          if (operation & OPERATIONS.FOLD) {
            stepState.isFolded = true;
          }

          if (operation & OPERATIONS.UNFOLD) {
            stepState.isFolded = false;
          }

          if (operation & OPERATIONS.HIGHLIGHT) {
            stepState.isHighlighted = true;
          }

          if (operation & OPERATIONS.LOWLIGHT) {
            stepState.isLowlighted = true;
          }
        });

        if (stepState.exists) {
          finalSteps[stepId] = stepState;
        }
      });

      return finalSteps;
    }, [block]);

    // Process REQ messages separately for user request boxes
    const userRequestBlocks = useMemo(() => {
      if (!block || !block.messages || !block.messages.length) {
        return [];
      }

      const requestBlock = {
        reqMessage: null,
        sttMessage: null,
      };

      block.messages.forEach((message) => {
        if (message.type === MESSAGE_TYPES.REQ) {
          requestBlock.reqMessage = message;
        } else if (message.type === MESSAGE_TYPES.REQ_STT) {
          requestBlock.sttMessage = message;
        }
      });

      if (requestBlock.reqMessage) {
        return [requestBlock];
      }

      return [];
    }, [block, block.messages.length]);

    const handleToggleExpanded = () => {
      setExpanded((prev) => !prev);
    };

    const toggleStepExpansion = useCallback(
      (stepId) => {
        setStepExpansionOverrides((prev) => {
          const step = processedSteps[stepId];
          if (!step) return prev;

          // Calculate current state: use override if exists, otherwise use !step.isFolded
          const hasOverride = Object.prototype.hasOwnProperty.call(
            prev,
            stepId
          );
          const currentExpanded = hasOverride ? prev[stepId] : !step.isFolded;

          return {
            ...prev,
            [stepId]: !currentExpanded,
          };
        });
      },
      [processedSteps]
    );

    // Handle copying message content to clipboard
    const handleCopyMessage = useCallback(async (htmlContent, fallbackText) => {
      try {
        // Try to copy both HTML and text formats
        if (navigator.clipboard && navigator.clipboard.write) {
          const clipboardItems = [];

          // Add HTML format
          if (htmlContent) {
            clipboardItems.push(
              new ClipboardItem({
                "text/html": new Blob([htmlContent], { type: "text/html" }),
                "text/plain": new Blob([fallbackText || htmlContent], {
                  type: "text/plain",
                }),
              })
            );
          }

          if (clipboardItems.length > 0) {
            await navigator.clipboard.write(clipboardItems);
          } else {
            // Fallback to text-only copy
            await navigator.clipboard.writeText(fallbackText || htmlContent);
          }
        } else {
          // Fallback to text-only copy for older browsers
          await navigator.clipboard.writeText(fallbackText || htmlContent);
        }
      } catch (err) {
        console.error("Failed to copy content: ", err);
        // Try fallback text copy
        try {
          await navigator.clipboard.writeText(fallbackText || htmlContent);
        } catch (fallbackErr) {
          console.error("Failed to copy fallback text: ", fallbackErr);
        }
      }
    }, []);

    // Render REQ messages as separate user request boxes
    const renderReqMessage = useCallback(
      (requestBlock) => {
        const message = requestBlock.reqMessage;
        if (!message) return null;

        const sttMessage = requestBlock.sttMessage;
        const text = sttMessage?.content || message.text;
        const audioFile = message.audioFile;
        const textToCopy = audioFile ? sttMessage?.content || "" : message.text;

        let htmlContent;
        if (audioFile) {
          htmlContent = `<span style="display:flex;align-items:center;gap:4px;"><svg style="width:24px;height:24px;fill:hsl(var(--foreground));" viewBox="0 0 24 24"><path d="M10 20h4c0 1.1-.9 2-2 2s-2-.9-2-2zm10-2.65V19c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2v-1.65l2-1.88v-5.15C6 7.4 7.56 5.1 10 4.34v-.38c0-1.42 1.17-2.59 2.99-2.59 1.82 0 2.99 1.17 2.99 2.59v.38c2.44.75 4 3.06 4 5.98v5.15l2 1.88z"/></svg>${
            sttMessage && sttMessage.content
              ? sttMessage.content
              : "Processing audio..."
          }</span>`;
        } else {
          htmlContent = convertTextWithXmlTagsToHtml(text);
        }

        return (
          <div
            key={`req-${message.step}-${message.order}`}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                maxWidth: "80%",
                padding: "10px",
                borderRadius: "16px",
                backgroundColor: getComponentBgColor(
                  "hsl(var(--primary) / 0.1)"
                ),
              }}
            >
              <MentionContentEditable
                html={htmlContent}
                disabled={true}
                onChange={() => {}} // No-op since it's disabled
              />
              {message.elapsed !== null && message.elapsed !== undefined && (
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "0.75rem",
                    marginTop: "4px",
                  }}
                >
                  {message.elapsed}ms
                </Typography>
              )}
            </div>
            <IconButton
              size="small"
              onClick={() => handleCopyMessage(htmlContent, textToCopy)}
              style={{
                marginTop: "4px",
                marginRight: "4px",
                color: "hsl(var(--muted-foreground))",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "hsl(var(--foreground))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "hsl(var(--muted-foreground))";
              }}
              title="Copy message"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
      [handleCopyMessage]
    );

    // Organize steps into hierarchy (parent -> children)
    const stepHierarchy = useMemo(() => {
      const rootSteps = [];
      const childSteps = {};

      Object.values(processedSteps).forEach((step) => {
        if (step.parentStep === -1) {
          rootSteps.push(step);
        } else {
          if (!childSteps[step.parentStep]) {
            childSteps[step.parentStep] = [];
          }
          childSteps[step.parentStep].push(step);
        }
      });

      // Sort by step number
      rootSteps.sort((a, b) => a.step - b.step);
      Object.keys(childSteps).forEach((parentId) => {
        childSteps[parentId].sort((a, b) => a.step - b.step);
      });

      return { rootSteps, childSteps };
    }, [processedSteps]);

    const renderStepBox = useCallback(
      (step, isChild = false) => {
        // Calculate expansion state: use override if exists, otherwise use !step.isFolded
        const hasOverride = Object.prototype.hasOwnProperty.call(
          stepExpansionOverrides,
          step.step
        );
        const isExpanded = hasOverride
          ? stepExpansionOverrides[step.step]
          : !step.isFolded;
        const hasChildren = stepHierarchy.childSteps[step.step]?.length > 0;
        const isNonFoldable = NON_FOLDABLE_TYPES.has(step.type);
        const isExpertStep = step.expertState !== null;

        // For foldable steps, use calculated expansion state; non-foldable always show content
        const showContent = isNonFoldable || isExpanded;

        // For non-foldable types, just render content directly without header
        if (isNonFoldable) {
          const {
            content: processedNonFoldableContent,
            components: nonFoldableComponents,
          } = processTags(step.content, onAssistantInteraction);

          return (
            <div
              key={`step-${step.step}`}
              style={{
                marginBottom: "8px",
                marginLeft: 0,
              }}
            >
              {step.content && step.content.trim() !== "" && (
                <div style={{ marginBottom: hasChildren ? "8px" : "0" }}>
                  <div
                    style={
                      {
                        // Content styles will be handled by ProcessedContent
                      }
                    }
                  >
                    <style>
                      {`
                        div[style*="Content"] p {
                          margin: 0;
                          overflow-wrap: break-word;
                        }
                        div[style*="Content"] pre {
                          white-space: pre-wrap;
                          overflow-wrap: break-word;
                        }
                      `}
                    </style>
                    <ProcessedContent
                      content={processedNonFoldableContent}
                      components={nonFoldableComponents}
                    />
                  </div>
                </div>
              )}
              {step.elapsed !== null && step.elapsed !== undefined && (
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "0.75rem",
                    marginTop: 0,
                    marginBottom: hasChildren ? "8px" : "0",
                    display: "block",
                  }}
                >
                  {step.elapsed}ms
                </Typography>
              )}

              {/* Child Steps */}
              {hasChildren && (
                <div
                  style={{
                    paddingLeft: "8px",
                    paddingBottom: "8px",
                  }}
                >
                  {stepHierarchy.childSteps[step.step].map((childStep) =>
                    renderStepBox(childStep, true)
                  )}
                </div>
              )}
            </div>
          );
        }

        // Get expert icon based on state
        const getExpertIcon = () => {
          if (!isExpertStep) return null;

          switch (step.expertState) {
            case "start":
              return (
                <CircularProgress
                  size={16}
                  style={{ color: "hsl(var(--primary))" }}
                />
              );
            case "complete":
              return (
                <CheckCircleIcon
                  fontSize="small"
                  style={{ color: "hsl(var(--green-500))" }}
                />
              );
            case "error":
              return (
                <ErrorIcon
                  fontSize="small"
                  style={{ color: "hsl(var(--destructive))" }}
                />
              );
            case "warn":
              return (
                <ReportProblemIcon
                  fontSize="small"
                  style={{ color: "hsl(var(--yellow-500))" }}
                />
              );
            case "stopped":
              return (
                <StopIcon
                  fontSize="small"
                  style={{ color: "hsl(var(--destructive))" }}
                />
              );
            default:
              return null;
          }
        };

        const expertIcon = getExpertIcon();

        return (
          <div
            key={`step-${step.step}`}
            style={{
              marginTop: isChild ? "8px" : "0",
              marginBottom: "8px",
              marginLeft: 0,
              border: step.isHighlighted
                ? "2px solid hsl(var(--yellow-500))"
                : "1px solid hsl(var(--border))",
              borderRadius: "16px",
              backgroundColor: "transparent",
            }}
          >
            {/* Step Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px",
                backgroundColor: "transparent",
                cursor: "pointer",
              }}
              onClick={() => toggleStepExpansion(step.step)}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {expertIcon && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {expertIcon}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: "hsl(var(--foreground))",
                      paddingLeft: "8px",
                      paddingRight: "8px",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      fontSize: "0.875rem",
                    }}
                  >
                    {isExpertStep ? step.expertContent : step.type}
                  </Typography>
                  {step.elapsed !== null && step.elapsed !== undefined && (
                    <Typography
                      variant="caption"
                      style={{
                        color: "hsl(var(--muted-foreground))",
                        fontSize: "0.7rem",
                      }}
                    >
                      {step.elapsed}ms
                    </Typography>
                  )}
                </div>
              </div>
              <IconButton
                size="small"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {showContent ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </div>

            {/* Step Content */}
            {(() => {
              const contentToRender = isExpertStep ? "" : step.content;
              const {
                content: processedContent,
                components: contentComponents,
              } = processTags(contentToRender, onAssistantInteraction);
              const hasContent =
                processedContent && processedContent.trim() !== "";
              const hasElapsed =
                step.elapsed !== null && step.elapsed !== undefined;
              const hasCollapsibleContent = hasContent || hasChildren;

              // Only render Collapse if there's content to show
              if (!hasCollapsibleContent) {
                return null;
              }

              return (
                <Collapse
                  in={showContent}
                  timeout="auto"
                  unmountOnExit
                  style={{
                    borderTop: "1px solid hsl(var(--border))",
                    borderColor: "hsl(var(--border))",
                    backgroundColor: "transparent",
                  }}
                >
                  <>
                    {/* Only render content box if there's actual content or elapsed time */}
                    {hasContent && (
                      <div style={{ padding: "16px" }}>
                        <div
                          style={
                            {
                              // Content styles will be handled by ProcessedContent
                            }
                          }
                        >
                          <style>
                            {`
                              div[style*="Content"] p {
                                margin: 0;
                                overflow-wrap: break-word;
                              }
                              div[style*="Content"] pre {
                                white-space: pre-wrap;
                                overflow-wrap: break-word;
                              }
                            `}
                          </style>
                          <ProcessedContent
                            content={processedContent}
                            components={contentComponents}
                          />
                        </div>
                        {hasElapsed && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "hsl(var(--muted-foreground))",
                              fontSize: "0.75rem",
                              marginTop: hasContent ? "4px" : "0",
                              display: "block",
                            }}
                          >
                            {step.elapsed}ms
                          </Typography>
                        )}
                      </div>
                    )}

                    {/* Child Steps */}
                    {hasChildren && (
                      <div
                        style={{
                          paddingLeft: "8px",
                          paddingBottom: "8px",
                        }}
                      >
                        {stepHierarchy.childSteps[step.step].map((childStep) =>
                          renderStepBox(childStep, true)
                        )}
                      </div>
                    )}
                  </>
                </Collapse>
              );
            })()}
          </div>
        );
      },
      [stepExpansionOverrides, stepHierarchy, expanded, toggleStepExpansion]
    );

    if (!block || !block.messages || block.messages.length === 0) {
      return null;
    }

    const hasSteps = Object.keys(processedSteps).length > 0;
    const isAssistantResponse = block.from === "assistant";
    const isBlockStopped = isStoppedLocal || isStopped;

    return (
      <div style={{ marginBottom: "16px", backgroundColor: "transparent" }}>
        {/* REQ Messages - Rendered outside main box for assistant responses */}
        {userRequestBlocks.map((requestBlock) =>
          renderReqMessage(requestBlock)
        )}

        <Paper
          elevation={0}
          style={{
            overflow: "hidden",
            borderRadius: "16px",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
          }}
        >
          {/* Block Content */}
          <div
            style={{
              padding: "8px",
              backgroundColor: "transparent",
            }}
          >
            {/* Assistant Steps - Regular step processing */}
            {hasSteps &&
              stepHierarchy.rootSteps.map((step) => renderStepBox(step))}
          </div>

          {/* Bottom Bar - Only show for assistant responses when complete or stopped */}
          {isAssistantResponse && (isComplete || isBlockStopped) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
                padding: "8px",
                backgroundColor: "transparent",
              }}
            >
              {!isBlockStopped && (
                <>
                  <IconButton
                    size="small"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "hsl(var(--green-500))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color =
                        "hsl(var(--muted-foreground))";
                    }}
                    title="Thumbs up"
                  >
                    <ThumbUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "hsl(var(--destructive))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color =
                        "hsl(var(--muted-foreground))";
                    }}
                    title="Thumbs down"
                  >
                    <ThumbDownIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              {isBlockStopped && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <WarningIcon
                    fontSize="small"
                    style={{ color: "hsl(var(--yellow-500))" }}
                  />
                  <Typography
                    variant="caption"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                      fontStyle: "italic",
                    }}
                  >
                    Request was cancelled
                  </Typography>
                </div>
              )}
            </div>
          )}
        </Paper>
      </div>
    );
  }),
  (prevProps, nextProps) => {
    // Custom comparison function for memo
    const prevBlock = prevProps.block;
    const nextBlock = nextProps.block;

    // Check if block content actually changed
    if (prevBlock.messages?.length !== nextBlock.messages?.length) {
      return false; // Re-render - messages count changed
    }

    // Compare messages content
    if (
      JSON.stringify(prevBlock.messages) !== JSON.stringify(nextBlock.messages)
    ) {
      return false; // Re-render - messages content changed
    }

    // Check if stopped state changed
    if (prevProps.isStopped !== nextProps.isStopped) {
      return false; // Re-render - stopped state changed
    }

    // No changes detected - skip re-render
    return true;
  }
);

ChatBlock.displayName = "ChatBlock";

export default ChatBlock;
